<template>
  <div class="ev-page-container ev-page-container--wide">
    <!-- edit user modal -->
    <modal
      name="edit-user-modal"
      :adaptive="true"
      :reset="true"
      height="auto"
      :resizable="true"
      :scrollable="true"
      :focusTrap="true"
      draggable=".window-header"
      :width="600"
      classes="user-modal"
    >
      <edit-user
        :selectedUser="selectedUser"
        :selectingEmployeeDisabled="true"
        :addOrEdit="false"
        :isSaveAndProceedButtonVisible="false"
        :departments="departments"
        :availableReports="availableReports"
        title="Uredi korisnika aplikacije"
        @saveAndCloseClicked="editUser"
      />
    </modal>

    <!-- add user modal -->
    <modal
      name="add-user-modal"
      :adaptive="true"
      :reset="true"
      height="auto"
      :resizable="true"
      :scrollable="true"
      :focusTrap="true"
      draggable=".window-header"
      :width="600"
      classes="user-modal"
    >
      <edit-user
        :selectedUser="selectedUser"
        :selectingEmployeeDisabled="false"
        :addOrEdit="true"
        :isSaveAndProceedButtonVisible="true"
        :departments="departments"
        :availableReports="availableReports"
        title="Dodaj korisnika aplikacije"
        @saveAndCloseClicked="addUser"
        @saveAndProceedClicked="addUserAndContinue"
      />
    </modal>

    <!-- delete user modal -->
    <modal
      name="delete-user-modal"
      :adaptive="true"
      :reset="true"
      :focusTrap="true"
      draggable=".window-header"
    >
      <delete-item
        message="Da li zaista žekite da obrišete korisnika aplikacije"
        title="Obriši korisnika"
        :selectedItem="selectedUser"
        @deleteButtonClicked="deleteUser"
      />
    </modal>

    <!-- Page Header -->
    <div class="ev-page-header">
      <h1 class="ev-page-title">
        <span class="ev-title-icon">👤</span>
        Korisnici
      </h1>
      <p class="ev-page-subtitle">Administracija korisnika portala</p>
    </div>

    <!-- Table Section -->
    <div class="ev-table-section">
      <div class="ev-table-header">
        <h3 class="ev-table-title">
          <span class="ev-table-icon">📋</span>
          Lista korisnika
          <span class="ev-table-count">{{ userCount }} korisnika</span>
        </h3>
      </div>
      <div class="ev-table-container">
        <vue-good-table
          theme="polar-bear"
          :columns="columns"
          :rows="rows"
          styleClass="vgt-table striped"
          :search-options="{
            enabled: true,
            searchFn: searchFunc,
            placeholder: 'Pretraži korisnike...',
          }"
        >
          <template slot="table-row" slot-scope="props">
            <span
              v-if="props.column.field == 'before'"
              class="ev-action-buttons"
            >
              <!-- edit icon -->
              <button
                class="ev-action-btn ev-action-btn-edit"
                v-show="props.row.isnew === false"
                @click="editRow(props.row)"
                title="Uredi"
              >
                <font-awesome-icon icon="edit" />
              </button>
              <!-- delete icon -->
              <button
                class="ev-action-btn ev-action-btn-delete"
                v-show="props.row.isnew === false"
                @click="deleteRow(props.row)"
                title="Obriši"
              >
                <font-awesome-icon icon="trash-alt" />
              </button>
              <!-- download credentials PDF icon -->
              <button
                class="ev-action-btn ev-action-btn-pdf"
                v-show="props.row.isnew === false && canReadPasswords"
                @click="downloadCredentialsPdf(props.row)"
                title="Preuzmi pristupne podatke (PDF)"
              >
                <font-awesome-icon icon="file-pdf" />
              </button>
              <!-- add new row button -->
              <button
                class="ev-btn ev-btn-primary"
                v-show="props.row.isnew === true"
                @click="addRow(props.row)"
              >
                <font-awesome-icon icon="plus" />
                Dodaj korisnika
              </button>
            </span>
            <span
              v-else-if="props.column.field == 'privilegeLevel'"
              class="ev-privilege-badge"
              :class="getPrivilegeBadgeClass(props.row.privilegeLevel)"
            >
              {{ getPrivilegeLevelLabel(props.row.privilegeLevel) }}
            </span>
            <span v-else>
              {{ props.formattedRow[props.column.field] }}
            </span>
          </template>
        </vue-good-table>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from "../../services/user.service";
import EditUser from "../parts/EditUser.vue";
import DeleteItem from "../parts/DeleteItem.vue";
import "vue-good-table/dist/vue-good-table.css";
import "vue-select/dist/vue-select.css";
import { VueGoodTable } from "vue-good-table";
import { UserBack, UserFront } from "../../models/user.js";
import { getPrivilegeLevelLabel } from "../../_helpers/privilegeLevels.js";

export default {
  components: {
    VueGoodTable,
    EditUser,
    DeleteItem,
  },
  name: "Users",
  data() {
    return {
      selectedUser: {},
      departments: [],
      availableReports: [],
      columns: [
        {
          label: "R.Br.",
          field: "userId",
          type: "number",
          sortable: false,
        },
        {
          label: "Ime",
          field: "firstName",
          sortable: false,
        },
        {
          label: "Prezime",
          field: "lastName",
          sortable: false,
        },
        {
          label: "Telefon",
          field: "phone",
          type: "string",
          sortable: false,
        },
        {
          label: "Korisničko ime",
          field: "userName",
          type: "string",
          sortable: false,
        },
        {
          label: "Nivo privilegija",
          field: "privilegeLevel",
          type: "string",
          sortable: false,
        },
        {
          label: "Akcije",
          field: "before",
          sortable: false,
        },
      ],
      rows: [],
      errorMessage: "",
    };
  },
  computed: {
    userCount() {
      return this.rows.filter((r) => !r.isnew).length;
    },
    canReadPasswords() {
      const user = this.$store.state.auth.user;
      if (!user || !user.specialCommand) {
        return false;
      }
      try {
        const specialCommand =
          typeof user.specialCommand === "string"
            ? JSON.parse(user.specialCommand)
            : user.specialCommand;
        return specialCommand.canReadPasswords === true;
      } catch (e) {
        return false;
      }
    },
  },
  mounted() {
    this.getUsers();
    this.getDepartments();
    this.getAvailableReports();
  },
  methods: {
    getPrivilegeLevelLabel(level) {
      return getPrivilegeLevelLabel(level);
    },
    getPrivilegeBadgeClass(level) {
      switch (level) {
        case 7:
          return "ev-privilege-badge--admin";
        case 5:
          return "ev-privilege-badge--super";
        case 1:
          return "ev-privilege-badge--user";
        case 0:
          return "ev-privilege-badge--readonly";
        default:
          return "";
      }
    },
    getDepartments() {
      UserService.getDepartments().then(
        (response) => {
          this.departments = response.data;
        },
        (error) => {
          console.error("Error loading departments:", error);
        }
      );
    },
    getAvailableReports() {
      UserService.getAvailableReports().then(
        (response) => {
          this.availableReports = response.data;
        },
        (error) => {
          console.error("Error loading available reports:", error);
        }
      );
    },
    collectErrorMessage(error) {
      let msg =
        (error.response &&
          error.response.data &&
          error.response.data.message) ||
        error.message ||
        error.toString();

      return msg;
    },
    resetUi() {
      this.rows = [];
    },
    searchFunc(row, col, cellValue, searchTerm) {
      if (typeof cellValue === "string" || cellValue instanceof String) {
        return cellValue.toLowerCase().includes(searchTerm.toLowerCase());
      } else if (cellValue == null) {
        return true;
      }
    },
    resetSelectedUser() {
      this.selectedUser = {};
    },
    getUsers() {
      UserService.getUsers().then(
        (response) => {
          // reset ui
          this.resetUi();

          // get new data
          // populate rows
          response.data.forEach((element) => {
            let usr = new UserFront(element);
            this.rows.push(usr);
          });

          // add "add" row
          this.rows.push(
            new UserFront({
              isnew: true,
            })
          );
        },
        (error) => {
          this.collectErrorMessage(error);
        }
      );
    },
    editRow(usr) {
      this.selectedUser = new UserFront(usr);
      this.$modal.show("edit-user-modal");
    },
    deleteRow(usr) {
      this.selectedUser = usr;
      this.$modal.show("delete-user-modal");
    },
    addRow(usr) {
      this.selectedUser = usr;
      this.$modal.show("add-user-modal");
    },
    editUser(user) {
      let userToEdit = new UserBack(user);

      UserService.putUser(userToEdit).then(
        () => {
          // reload ui
          this.resetUi();

          this.getUsers();
        },
        (error) => {
          this.errorMessage = this.collectErrorMessage(error);
        }
      );

      this.$modal.hide("edit-user-modal");
    },
    deleteUser() {
      let userToDelete = new UserBack(this.selectedUser);

      UserService.deleteUser(userToDelete).then(
        () => {
          // reload depts
          this.resetUi();

          this.getUsers();
        },
        (error) => {
          this.errorMessage = this.collectErrorMessage(error);
        }
      );
      this.$modal.hide("delete-user-modal");
    },
    addUserAndContinue(user) {
      // create new employer
      let newUser = new UserBack(user);

      // upload
      // call axios
      UserService.postUser(newUser).then(() => {
        // reset ui
        this.resetUi();

        this.getUsers();
      });
    },
    addUser(user) {
      // create new employer
      let newUser = new UserBack(user);

      // upload
      // call axios
      UserService.postUser(newUser).then(() => {
        // reset ui
        this.resetUi();

        this.getUsers();

        this.$modal.hide("add-user-modal");
      });
    },
    downloadCredentialsPdf(user) {
      UserService.downloadUserCredentialsPdf(user.userId).then(
        (response) => {
          // Create blob link to download
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute(
            "download",
            `pristupni_podaci_${user.firstName}_${user.lastName}.pdf`
          );
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        },
        (error) => {
          console.error("Error downloading PDF:", error);
          alert("Greška pri preuzimanju PDF-a");
        }
      );
    },
  },
};
</script>

<style scoped>
/* Component-specific styles */
.user-modal {
  max-height: 85vh !important;
  overflow-y: auto !important;
}

/* Privilege Level Badges */
.ev-privilege-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ev-privilege-badge--admin {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: white;
}

.ev-privilege-badge--super {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  color: white;
}

.ev-privilege-badge--user {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  color: white;
}

.ev-privilege-badge--readonly {
  background: #e2e8f0;
  color: #64748b;
}

/* PDF Download Button */
.ev-action-btn-pdf {
  background: #fef3c7;
  color: #d97706;
}

.ev-action-btn-pdf:hover {
  background: #f59e0b;
  color: white;
}
</style>
